import { COLORS } from '../../constants/theme';
import { useGatherIntel } from '../../hooks/useGatherIntel';
import { IntelFreshnessIndicator } from '../attack/IntelFreshnessIndicator';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import type { SpyOnMilitaryData, IntelReportDto } from '../../types/intel';

const SM_FG = '#8ec4a0';
const OFF_CLR = '#e8a04f';

interface MobileSpyOnMilitaryCardProps {
  worldId: string;
  playerProvinceId: string;
  targetProvinceId: string;
  data: SpyOnMilitaryData | null;
  report: IntelReportDto | null;
  isLoading: boolean;
  error: string | null;
  /** SF-105: disables the Gather button when the target is protected — thievery has no intel
   *  exemption (unlike Crystal Ball). */
  disabled?: boolean;
  onResult: (message: string, type: 'success' | 'failure' | 'warning') => void;
  onGathered: () => void;
  /** SF-157: per-report Share/Delete, gated on report.sharing === 'PRIVATE' for Share. Omitted
   *  (undefined/falsy) by Target Finder's existing usage — no icons render there. */
  onShare?: () => void;
  onDelete?: () => void;
  /** SF-157: when true, suppresses only the Gather/Refresh button and its trigger — the
   *  `useGatherIntel` hook itself is still called unconditionally (Rules of Hooks). Renders all
   *  other content (Networth/Army Roster/etc.) exactly as it does live. */
  readOnly?: boolean;
}

const REASON_ID = 'som-protected-reason';

/**
 * SF-171 Component 9: Spy on Military mobile card. Own headline (Raw Offense/Raw Defense — both
 * unmodified raw quantities, "Net" was always the wrong word, SF-157 Critical #2), then only what
 * Crystal Ball can't tell you — ME modifiers, army home/out status, army return timing, Army
 * Roster, and (depends on SF-170) a Training Queue section. Degrades gracefully when SF-170 hasn't
 * shipped yet: the section is simply omitted when `data.trainingUnits` is absent/null, never
 * falling back to the mislabeled construction-sourced `hasTrainingQueue`/`queueLength`/
 * `nextCompletionTicks`.
 */
export function MobileSpyOnMilitaryCard({
  worldId, playerProvinceId, targetProvinceId, data, report, isLoading, error, disabled, onResult, onGathered,
  onShare, onDelete, readOnly,
}: MobileSpyOnMilitaryCardProps) {
  const { gather, state } = useGatherIntel(worldId, playerProvinceId, targetProvinceId, { onResult, onGathered });
  const isGathering = state.SOM === 'loading';
  const isDisabled = isGathering || disabled;

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10, opacity: readOnly ? 0.85 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(74,122,90,0.15)', borderBottom: `1px solid ${COLORS.border}` }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: SM_FG, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Spy on Military</span>
          {readOnly && (
            <span style={{ fontSize: 9, color: COLORS.text.muted, border: `1px solid ${COLORS.border}`, borderRadius: 3, padding: '1px 4px', textTransform: 'uppercase' }}>
              Archived
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {report && <IntelFreshnessIndicator capturedAt={report.capturedAt} />}
          {onShare && report?.sharing === 'PRIVATE' && (
            <button
              type="button"
              aria-label="Share this Spy on Military report"
              onClick={onShare}
              style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.positive, cursor: 'pointer', fontSize: 14 }}
            >
              ⤴
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label="Delete this Spy on Military report"
              onClick={onDelete}
              style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.negative, cursor: 'pointer', fontSize: 14 }}
            >
              🗑
            </button>
          )}
          {data && !readOnly && (
            <button
              type="button"
              aria-label="Refresh Spy on Military intel"
              aria-describedby={disabled ? REASON_ID : undefined}
              onClick={() => gather('SOM')}
              disabled={isDisabled}
              style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: SM_FG, cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: 16, opacity: isDisabled ? 0.4 : 1 }}
            >
              ⟳
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {isLoading && !data && (
          <p style={{ color: COLORS.text.muted, fontSize: 12, margin: 0 }}>Loading...</p>
        )}
        {error && !data && (
          <p style={{ color: COLORS.negative, fontSize: 12, margin: 0 }}>Failed to load Spy on Military intel.</p>
        )}
        {!isLoading && !error && !data && (
          <>
            <p style={{ color: COLORS.text.muted, fontSize: 12, marginTop: 0 }}>
              Reveals military efficiency, army status, and incoming troops.
            </p>
            <button
              type="button"
              onClick={() => gather('SOM')}
              disabled={isDisabled}
              aria-describedby={disabled ? REASON_ID : undefined}
              style={{
                width: '100%', minHeight: 44, borderRadius: 6, border: 'none',
                background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 13, fontWeight: 700,
                cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1,
              }}
            >
              {isGathering ? 'Sending thieves...' : 'Gather Spy on Military'}
            </button>
            {disabled && (
              <p id={REASON_ID} style={{ color: COLORS.text.muted, fontSize: 11, marginTop: 6 }}>
                Target is protected — thievery-based intel unavailable.
              </p>
            )}
          </>
        )}
        {data && (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <Headline label="Raw Offense" value={data.offensePoints != null ? formatNumber(data.offensePoints) : '—'} color={OFF_CLR} />
              <Headline label="Raw Defense" value={data.defensePoints != null ? formatNumber(data.defensePoints) : '—'} color={COLORS.link} />
            </div>

            {data.offensiveME != null && <LV label="Offense ME (%)" value={`${(data.offensiveME * 100).toFixed(0)}`} color={COLORS.positive} />}
            <LV label="Defense ME (%)" value={`${(data.defensiveME * 100).toFixed(0)}`} color={data.defensiveME > 1 ? COLORS.warning : COLORS.text.secondary} />

            {data.networth != null && (
              <>
                <GroupLabel text="Networth" />
                <LV label="Networth" value={formatNumber(data.networth)} color={COLORS.gold} />
              </>
            )}

            <GroupLabel text="Army Roster" />
            {data.generals != null && <LV label="Generals" value={formatNumber(data.generals)} />}
            <LV label="Soldiers" value={formatNumber(data.soldiers)} />
            <LV label="Offense Specialists" value={formatNumber(data.offensiveSpecs)} />
            <LV label="Defense Specialists" value={formatNumber(data.defensiveSpecs)} />
            <LV label="Elites" value={formatNumber(data.elites)} />

            <GroupLabel text="Army Status" />
            <LV label="Army" value={data.hasArmiesOut ? 'Out' : 'Home'} color={data.hasArmiesOut ? COLORS.warning : COLORS.positive} />
            {data.hasArmiesOut && data.nextReturnTicks != null && (
              <LV label="Returns In" value={formatDuration(data.nextReturnTicks)} />
            )}

            {data.trainingUnits != null && data.trainingUnits.length > 0 && (
              <>
                <GroupLabel text="Training Queue" />
                {data.trainingUnits.map((t, i) => (
                  <LV key={i} label={formatEnumLabel(t.unitType)} value={`${formatNumber(t.unitCount)} · ${formatDuration(t.ticksRemaining)}`} />
                ))}
              </>
            )}

            {disabled && (
              <p id={REASON_ID} style={{ color: COLORS.text.muted, fontSize: 11, marginTop: 10 }}>
                Target is protected — thievery-based intel unavailable.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Headline({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ flex: 1, background: COLORS.cardAlt, borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ color: COLORS.text.tertiary, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color, fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}

function GroupLabel({ text }: { text: string }) {
  return (
    <div style={{ color: COLORS.text.tertiary, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '10px 0 4px', borderTop: `1px solid ${COLORS.border}`, paddingTop: 8 }}>
      {text}
    </div>
  );
}

function LV({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span style={{ color: COLORS.text.tertiary, fontSize: 12 }}>{label}</span>
      <span style={{ color: color ?? COLORS.text.primary, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    </div>
  );
}
